import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import psychosocialService from '../../services/psychosocialService';
import { PsychosocialPublicFormData, PsychosocialScaleType } from '../../types/psychosocial';

const frequencyLabels = ['Sempre', 'Frequentemente', 'Às vezes', 'Raramente', 'Nunca/quase nunca'];
const intensityLabels = ['Em grande medida', 'Em boa medida', 'Em certa medida', 'Em pequena medida', 'Em nenhuma medida'];

const getLabels = (scaleType: PsychosocialScaleType) =>
  scaleType === 'frequency' ? frequencyLabels : intensityLabels;

/** A página é pública: não há PageLayout/AppBar. Reproduz o `Container` centralizado. */
const NARROW_PAGE_SX = { maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 8 } as const;
const WIDE_PAGE_SX = { maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 } as const;

const getResponseData = (err: unknown): { error?: unknown; code?: unknown } =>
  (err as { response?: { data?: { error?: unknown; code?: unknown } } })?.response?.data ?? {};

const PsychosocialRespondPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [formData, setFormData] = useState<PsychosocialPublicFormData | null>(null);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [currentDimension, setCurrentDimension] = useState(0);
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const loadForm = useCallback(async () => {
    setLoading(true);
    try {
      const data = await psychosocialService.loadPublicForm(token!);
      setFormData(data);
      if (data.draft) {
        setAnswers(data.draft.answers);
        setStarted(true);
      }
    } catch (err: unknown) {
      const rawCode = getResponseData(err).code;
      const code = typeof rawCode === 'string' ? rawCode : null;
      setErrorCode(code);
      if (code === 'EXPIRED') {
        setError('Este link expirou. Solicite um novo link ao gestor da campanha.');
      } else if (code === 'ALREADY_COMPLETED') {
        setError('Este questionário já foi respondido. Obrigado pela participação!');
      } else {
        setError('Link inválido ou erro ao carregar o formulário.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  const handleAnswer = (questionId: number, value: number) => {
    setAnswers((prev) => ({ ...prev, [String(questionId)]: value }));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await psychosocialService.saveDraft(token!, answers);
    } catch {
      // silent fail for draft
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await psychosocialService.submitResponse(token!, answers);
      setSubmitted(true);
    } catch (err: unknown) {
      const responseError = getResponseData(err).error;
      setError(typeof responseError === 'string' && responseError ? responseError : 'Erro ao enviar respostas');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={NARROW_PAGE_SX}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" gutterBottom>
              {errorCode === 'ALREADY_COMPLETED' ? 'Questionário Respondido' : 'Avaliação Indisponível'}
            </Typography>
            <Alert severity={errorCode === 'ALREADY_COMPLETED' ? 'success' : 'warning'} sx={{ mt: 2 }}>
              {error}
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={NARROW_PAGE_SX}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h4" gutterBottom color="success.main">
              Obrigado!
            </Typography>
            <Typography variant="h6" gutterBottom>
              Suas respostas foram enviadas com sucesso.
            </Typography>
            <Typography color="text.secondary">
              Suas respostas contribuem para a melhoria das condições de trabalho na sua empresa.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!formData) return null;

  const dimensions = formData.form.dimensions;
  const totalQuestions = dimensions.reduce((s, d) => s + d.questions.length, 0);
  const answeredCount = Object.keys(answers).length;
  const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  // Welcome screen
  if (!started) {
    return (
      <Box sx={NARROW_PAGE_SX}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" gutterBottom>
              Avaliação de Riscos Psicossociais
            </Typography>
            <Typography variant="h6" color="primary" gutterBottom>
              {formData.campaign.companyName}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              {formData.campaign.name}
            </Typography>
            {formData.campaign.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {formData.campaign.description}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Este questionário faz parte da avaliação de riscos psicossociais da sua empresa, em conformidade com a NR-1.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Suas respostas são importantes para melhorar as condições de trabalho.
            </Typography>
            <Box sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Tempo estimado: ~{formData.estimatedMinutes} minutos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Você pode salvar e continuar depois
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={() => setStarted(true)}
            >
              Iniciar Avaliação
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const currentDim = dimensions[currentDimension];
  if (!currentDim) return null;

  const isLastDimension = currentDimension === dimensions.length - 1;

  return (
    <Box sx={WIDE_PAGE_SX}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {formData.campaign.companyName} - {formData.campaign.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentDimension + 1} de {dimensions.length} seções
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
          {answeredCount} de {totalQuestions} perguntas respondidas ({Math.round(progress)}%)
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {currentDim.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {currentDim.domain}
          </Typography>

          {currentDim.questions.map((q, qIdx) => {
            const labels = getLabels(q.scaleType);
            return (
              <Box key={q.id} sx={{ mb: 3, pb: 2, borderBottom: qIdx < currentDim.questions.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                <Typography sx={{ mb: 1.5 }}>
                  {qIdx + 1}. {q.text}
                </Typography>
                <RadioGroup
                  row
                  value={answers[String(q.id)] ?? ''}
                  onChange={(e) => handleAnswer(q.id, Number(e.target.value))}
                >
                  {labels.map((label, idx) => (
                    <FormControlLabel
                      key={idx + 1}
                      value={idx + 1}
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">{label}</Typography>}
                      sx={{ mr: 2 }}
                    />
                  ))}
                </RadioGroup>
              </Box>
            );
          })}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {currentDimension > 0 && (
            <Button onClick={() => setCurrentDimension((p) => p - 1)}>
              Anterior
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSaveDraft}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar Rascunho'}
          </Button>
        </Box>
        {isLastDimension ? (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={saving || answeredCount < totalQuestions}
          >
            {saving ? 'Enviando...' : 'Enviar Respostas'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => setCurrentDimension((p) => p + 1)}
          >
            Próxima Seção
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default PsychosocialRespondPage;
